import { useState } from "react";

function buildMonthCells(monthValue) {
  const monthDate = new Date(`${monthValue}T00:00:00`);
  const monthStart = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
  const startOffset = monthStart.getDay();
  const gridStart = new Date(monthStart);
  gridStart.setDate(monthStart.getDate() - startOffset);

  return Array.from({ length: 42 }, (_, index) => {
    const cellDate = new Date(gridStart);
    cellDate.setDate(gridStart.getDate() + index);
    return cellDate;
  });
}

function toISODate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function sameMonth(date, monthValue) {
  const monthDate = new Date(`${monthValue}T00:00:00`);
  return date.getFullYear() === monthDate.getFullYear() && date.getMonth() === monthDate.getMonth();
}

export function CalendarSection({ calendar }) {
  if (!calendar) return null;

  const monthOptions = Array.from(new Set(calendar.events.map((event) => event.date.slice(0, 7)))).sort();
  const [selectedMonth, setSelectedMonth] = useState(monthOptions[0] || "2026-08");
  const monthValue = `${selectedMonth}-01`;
  const cells = buildMonthCells(monthValue);
  const weekdays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const selectedMonthEvents = calendar.events.filter((event) => event.date.startsWith(selectedMonth));
  const eventsByDate = calendar.events.reduce((map, event) => {
    const list = map.get(event.date) || [];
    list.push(event);
    map.set(event.date, list);
    return map;
  }, new Map());

  return (
    <section className="calendar-shell">
      <div className="calendar-top">
        <div>
          <span className="section-eyebrow">{calendar.focusLabel || "Calendar"}</span>
          <h3>{calendar.monthLabel}</h3>
        </div>
        <div className="calendar-tabs" aria-label="Calendar months">
          {monthOptions.map((month) => {
            const date = new Date(`${month}-01T00:00:00`);
            return (
              <button
                key={month}
                type="button"
                className={selectedMonth === month ? "active" : ""}
                onClick={() => setSelectedMonth(month)}
              >
                {date.toLocaleDateString(undefined, { month: "short", year: "numeric" })}
              </button>
            );
          })}
        </div>
      </div>

      <div className="calendar-layout">
        <div className="calendar-grid-wrap">
          <div className="calendar-grid weekdays">
            {weekdays.map((weekday) => (
              <div key={weekday} className="calendar-weekday">
                {weekday}
              </div>
            ))}
          </div>
          <div className="calendar-grid">
            {cells.map((date) => {
              const dateKey = toISODate(date);
              const dayEvents = eventsByDate.get(dateKey) || [];
              const inMonth = sameMonth(date, monthValue);
              return (
                <div
                  key={dateKey}
                  className={`calendar-day ${inMonth ? "" : "muted"} ${dayEvents.length ? "has-event" : ""}`}
                >
                  <div className="calendar-day-top">
                    <span>{date.getDate()}</span>
                    {dayEvents.length ? <strong>{dayEvents.length}</strong> : null}
                  </div>
                  <div className="calendar-day-events">
                    {dayEvents.slice(0, 2).map((event) => (
                      <a
                        key={`${dateKey}-${event.title}`}
                        href={event.sourceUrl}
                        className={`event-pill ${event.color}`}
                        title={`${event.title} - ${event.window}`}
                      >
                        {event.tag}
                      </a>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <aside className="calendar-agenda">
          <h4>This month</h4>
          <p>Action list for the selected month.</p>
          <div className="agenda-list">
            {selectedMonthEvents.length ? (
              selectedMonthEvents.map((event) => (
                <article key={event.date + event.title} className={`agenda-item ${event.color}`}>
                  <div className="agenda-item-head">
                    <strong>{event.title}</strong>
                    <span>{event.window}</span>
                  </div>
                  <p>{event.note}</p>
                  <a href={event.sourceUrl}>{event.sourceName}</a>
                </article>
              ))
            ) : (
              <article className="agenda-item quiet">
                <strong>No major action</strong>
                <p>Use this month for reading, writing and revision.</p>
              </article>
            )}
          </div>
        </aside>
      </div>
    </section>
  );
}
